import { Component, OnInit, OnDestroy, DestroyRef, NgZone, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { TableModule } from 'primeng/table';
import { Button } from 'primeng/button';
import { Dialog } from 'primeng/dialog';
import { Select } from 'primeng/select';
import { InputText } from 'primeng/inputtext';
import { Checkbox } from 'primeng/checkbox';
import { Tooltip } from 'primeng/tooltip';
import { MessageService, ConfirmationService } from 'primeng/api';
import { ConfirmDialog } from 'primeng/confirmdialog';
import { catchError, of, forkJoin } from 'rxjs';
import { PresalesService } from '../../services/presales.service';
import { ServiceGroupService } from '../../services/service-group.service';
import {
  PresalesClient,
  PresalesCostCenter,
  CreatePresalesCostCenter
} from '../../models/presales.model';
import { ServiceGroup } from '../../models/service-group.model';
import { PageHeaderSearchActionService } from '../../services/page-header-search-action.service';
import { AuthService } from '../../services/auth.service';

interface Option { label: string; value: string; }

@Component({
  selector: 'app-presales-cost-center-management',
  standalone: true,
  imports: [
    CommonModule, FormsModule, TableModule, Button, Dialog, Select,
    InputText, Checkbox, Tooltip, ConfirmDialog
  ],
  providers: [ConfirmationService],
  templateUrl: './presales-cost-center-management.component.html',
  styleUrl: './presales-cost-center-management.component.scss'
})
export class PresalesCostCenterManagementComponent implements OnInit, OnDestroy {
  private destroyRef = inject(DestroyRef);

  costCenters: PresalesCostCenter[] = [];
  filteredCostCenters: PresalesCostCenter[] = [];
  clients: PresalesClient[] = [];
  serviceGroups: ServiceGroup[] = [];
  loading = true;
  searchText = '';

  // Filters
  selectedStatus: 'active' | 'inactive' | null = null;
  selectedClientFilter: string | null = null;

  // Dropdown options
  clientOptions: Option[] = [];
  serviceGroupOptions: Option[] = [];

  statusFilterOptions: Option[] = [
    { label: 'Active',   value: 'active' },
    { label: 'Inactive', value: 'inactive' },
  ];

  // Dialog state
  displayDialog = false;
  isEditMode = false;
  selectedCostCenter: PresalesCostCenter = this.emptyCostCenter();
  fieldTouched: Record<string, boolean> = {};

  // Access — Admin + PM can add / edit (matches PresalesAccess policy)
  canManagePresales = false;

  constructor(
    private presalesService: PresalesService,
    private serviceGroupService: ServiceGroupService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private authService: AuthService,
    public pageHeaderService: PageHeaderSearchActionService,
    private ngZone: NgZone
  ) {}

  ngOnInit(): void {
    this.pageHeaderService.setCanShowFilterToggle(true);
    this.pageHeaderService.setPlaceholder('Search presales cost centers…');
    this.pageHeaderService.setPrimaryButtonLabel('Add Presales Cost Center');
    this.pageHeaderService.setCanShowExport(true);
    this.pageHeaderService.setExportDisabled(true);

    this.authService.user$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(user => {
      this.canManagePresales = !!(user?.isAdmin || user?.isPM);
      this.pageHeaderService.setCanShowPrimaryAction(this.canManagePresales);
    });

    this.pageHeaderService.searchText.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(v => { this.searchText = v ?? ''; this.applyFilters(); });

    this.pageHeaderService.primaryActionClick.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.showAddDialog());

    this.pageHeaderService.exportClick.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.ngZone.run(() => this.exportToExcel()));

    this.loadData();
  }

  ngOnDestroy(): void {
    this.pageHeaderService.clearPageConfig();
  }

  loadData(): void {
    this.loading = true;
    forkJoin({
      costCenters:   this.presalesService.getAllCostCenters().pipe(catchError(() => of([] as PresalesCostCenter[]))),
      clients:       this.presalesService.getAllClients().pipe(catchError(() => of([] as PresalesClient[]))),
      serviceGroups: this.serviceGroupService.getAllServiceGroups().pipe(catchError(() => of([] as ServiceGroup[]))),
    })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(data => {
        this.costCenters = data.costCenters || [];
        this.clients = (data.clients || []).filter(c => c.isActive);
        this.serviceGroups = (data.serviceGroups || []).filter(sg => sg.isActive);

        this.clientOptions = this.clients.map(c => ({ label: c.name, value: c.id }));
        this.serviceGroupOptions = this.serviceGroups.map(sg => ({ label: sg.name, value: sg.id }));

        this.applyFilters();
        this.loading = false;
      });
  }

  applyFilters(): void {
    let list = [...this.costCenters];
    const s = this.searchText.trim().toLowerCase();
    if (s) {
      list = list.filter(c =>
        c.name?.toLowerCase().includes(s) ||
        c.description?.toLowerCase().includes(s) ||
        c.presalesClientName?.toLowerCase().includes(s) ||
        c.serviceGroupName?.toLowerCase().includes(s)
      );
    }
    if (this.selectedStatus === 'active') {
      list = list.filter(c => c.isActive === true);
    } else if (this.selectedStatus === 'inactive') {
      list = list.filter(c => c.isActive === false);
    }
    if (this.selectedClientFilter) {
      list = list.filter(c => c.presalesClientId === this.selectedClientFilter);
    }
    this.filteredCostCenters = list;
    this.pageHeaderService.setExportDisabled(this.filteredCostCenters.length === 0);
  }

  clearFilters(): void {
    this.selectedStatus = null;
    this.selectedClientFilter = null;
    this.pageHeaderService.setSearchText('');
    this.applyFilters();
  }

  hasActiveFilters(): boolean {
    return !!(this.selectedStatus || this.selectedClientFilter);
  }

  exportToExcel(): void {
    if (!this.filteredCostCenters.length) return;
    const rows = this.filteredCostCenters.map(c => ({
      'Name':          c.name,
      'Description':   c.description || '',
      'Client':        c.presalesClientName || 'Shared (all clients)',
      'Service Group': c.serviceGroupName || '',
      'Active':        c.isActive ? 'Yes' : 'No',
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb: XLSX.WorkBook = {
      Sheets: { 'Presales Cost Centers': ws },
      SheetNames: ['Presales Cost Centers']
    };
    const buffer: unknown = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([buffer as ArrayBuffer], { type: 'application/octet-stream' });
    const d = new Date();
    const stamp = `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, '0')}${String(d.getDate()).padStart(2, '0')}`;
    saveAs(blob, `Presales_Cost_Centers_${stamp}.xlsx`);
  }

  // ================= Stats =================

  get totalCount(): number { return this.costCenters.length; }
  get sharedCount(): number { return this.costCenters.filter(c => !c.presalesClientId).length; }

  // ================= Dialog / CRUD =================

  showAddDialog(): void {
    this.selectedCostCenter = this.emptyCostCenter();
    this.isEditMode = false;
    this.fieldTouched = {};
    this.displayDialog = true;
  }

  showEditDialog(costCenter: PresalesCostCenter): void {
    this.selectedCostCenter = { ...costCenter };
    this.isEditMode = true;
    this.fieldTouched = {};
    this.displayDialog = true;
  }

  hideDialog(): void {
    this.displayDialog = false;
  }

  saveCostCenter(): void {
    this.fieldTouched['name'] = true;
    if (!this.selectedCostCenter.name?.trim()) return;

    const dto: CreatePresalesCostCenter = {
      name:             this.selectedCostCenter.name.trim(),
      description:      this.selectedCostCenter.description || undefined,
      presalesClientId: this.selectedCostCenter.presalesClientId || null,
      serviceGroupId:   this.selectedCostCenter.serviceGroupId || null,
      isActive:         this.selectedCostCenter.isActive
    };

    if (this.isEditMode && this.selectedCostCenter.id) {
      this.presalesService.updateCostCenter(this.selectedCostCenter.id, dto)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (saved) => {
            const idx = this.costCenters.findIndex(c => c.id === saved.id);
            if (idx >= 0) this.costCenters[idx] = saved;
            this.applyFilters();
            this.messageService.add({ severity: 'success', summary: 'Updated', detail: `"${saved.name}" saved` });
            this.hideDialog();
          },
          error: () => this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to update cost center' })
        });
    } else {
      this.presalesService.addCostCenter(dto)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (created) => {
            this.costCenters = [created, ...this.costCenters];
            this.applyFilters();
            this.messageService.add({ severity: 'success', summary: 'Created', detail: `"${created.name}" added` });
            this.hideDialog();
          },
          error: () => this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to add cost center' })
        });
    }
  }

  /**
   * Flip the cost center's active state via the standard update endpoint —
   * same pattern as presales-client-management.toggleActive.
   */
  toggleActive(costCenter: PresalesCostCenter): void {
    const activating = !costCenter.isActive;
    this.confirmationService.confirm({
      message: `Are you sure you want to ${activating ? 'activate' : 'deactivate'} "${costCenter.name}"?`,
      header: activating ? 'Confirm Activate' : 'Confirm Deactivate',
      icon: 'pi pi-exclamation-triangle',
      acceptButtonStyleClass: activating ? 'p-button-success' : 'p-button-danger',
      accept: () => {
        const dto: CreatePresalesCostCenter = {
          name:             costCenter.name,
          description:      costCenter.description || undefined,
          presalesClientId: costCenter.presalesClientId || null,
          serviceGroupId:   costCenter.serviceGroupId || null,
          isActive:         !costCenter.isActive,
        };

        this.presalesService.updateCostCenter(costCenter.id, dto)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe({
            next: (saved) => {
              const idx = this.costCenters.findIndex(c => c.id === saved.id);
              if (idx >= 0) this.costCenters[idx] = saved;
              this.applyFilters();
              this.messageService.add({
                severity: 'success',
                summary: 'Success',
                detail: `Presales cost center ${saved.isActive ? 'activated' : 'deactivated'} successfully`
              });
            },
            error: () => this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: 'Failed to update cost center status'
            })
          });
      }
    });
  }

  // ================= Helpers =================

  private emptyCostCenter(): PresalesCostCenter {
    return {
      id: '',
      name: '',
      description: '',
      presalesClientId: null,
      presalesClientName: null,
      serviceGroupId: null,
      serviceGroupName: null,
      isActive: true,
      promotedToCostCenterId: null,
      promotedToCostCenterName: null
    };
  }
}
